import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Building2, Loader2, Pencil, Save, Users } from 'lucide-react';
import { useProspecting } from '../../context/ProspectingContext';

export default function ProspectingTopBar({
  search,
  onSaveList,
  savingList = false,
  savedListActive = false,
  onShowResults,
}) {
  const { renameSearch } = useProspecting();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(search.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(search.name);
  }, [search.name, editing]);

  useEffect(() => {
    if (editing) {
      // Focus on next frame to avoid the same-commit blur trap from CLAUDE.md
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== search.name) {
      renameSearch(search.id, trimmed);
    } else {
      setDraft(search.name);
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(search.name);
    setEditing(false);
  };

  const Icon = search.type === 'companies' ? Building2 : Users;
  const typeLabel = search.type === 'companies' ? 'Companies' : 'People';

  return (
    <div className="h-12 flex items-center gap-3 px-4 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 flex-shrink-0">
          <Icon className="w-3 h-3" />
          {typeLabel}
        </div>
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            className="text-sm font-semibold text-gray-900 dark:text-white bg-transparent border border-brand-300 dark:border-brand-700 rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:focus:ring-brand-900/40 min-w-0 flex-1 max-w-md"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white truncate min-w-0 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
          >
            <span className="truncate">{search.name}</span>
            <Pencil className="w-3 h-3 text-gray-300 group-hover:text-brand-500 transition-colors flex-shrink-0" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {savedListActive && (
          <button
            type="button"
            onClick={onShowResults}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Results
          </button>
        )}
        <button
          type="button"
          onClick={onSaveList}
          disabled={savingList}
          className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 dark:bg-white px-2.5 py-1.5 text-xs font-medium text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {savingList ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          Save list
        </button>
      </div>
    </div>
  );
}
